import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Paginado } from '../api/client';
import { Campo, Carregando, ErroBox, Modal, ModalRodape } from './ui';

export type ModoDeDisparo = 'iniciar' | 'agendar';

interface Recusa {
  participantId: string;
  nome: string;
  motivo: string;
}

interface Resultado {
  quantos: number;
  para?: string;
  recusadas: Recusa[];
}

/**
 * Só evento Ativo aparece na escolha: é o único em que a resposta da pessoa
 * entra na conversa (17/10/2026 — ver `garantirEventoAtivo` na API).
 */
const SITUACAO_QUE_CONVIDA = 'ATIVO';

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

/**
 * Iniciar ou agendar jornadas — a mesma janela na tela de Jornadas do evento e
 * em Agendamentos (16/10/2026).
 *
 * Em Agendamentos não há evento no endereço, e a janela pergunta qual. Fora
 * isso é o mesmo pedido, com o mesmo aviso: um componente só para que as duas
 * portas não comecem a divergir no que dizem a quem está prestes a mandar
 * mensagem para gente de verdade.
 *
 * Termina numa confirmação com o número de pessoas, e não fechando calada: em
 * Agendamentos, "iniciar" não deixa rastro na própria tela — sem o retorno,
 * quem clicou não teria como saber se saiu alguma coisa.
 */
export function DisparoDeJornadas({
  modo,
  eventId,
  aoFechar,
}: {
  modo: ModoDeDisparo;
  /** Ausente = a janela pergunta o evento. */
  eventId?: string;
  aoFechar: () => void;
}) {
  const cliente = useQueryClient();
  const [resultado, setResultado] = useState<Resultado | null>(null);

  const eventos = useQuery({
    queryKey: ['eventos', 'para-disparo'],
    queryFn: () =>
      api<Paginado<{ id: string; name: string; status: string }>>('/events?page=1&perPage=100'),
    enabled: !eventId,
  });

  const disparar = useMutation({
    mutationFn: async ({ evento, quando }: { evento: string; quando?: string }): Promise<Resultado> => {
      if (modo === 'agendar') {
        const r = await api<{ agendadas: number; para: string; recusadas: Recusa[] }>(
          `/events/${evento}/journeys/agendar`,
          { method: 'POST', body: { todos: true, quando } },
        );
        return { quantos: r.agendadas, para: r.para, recusadas: r.recusadas };
      }

      const r = await api<{ iniciadas: number; recusadas: Recusa[] }>(
        `/events/${evento}/journeys/iniciar`,
        { method: 'POST', body: { todos: true } },
      );
      return { quantos: r.iniciadas, recusadas: r.recusadas };
    },
    onSuccess: (r) => {
      cliente.invalidateQueries({ queryKey: ['jornadas'] });
      cliente.invalidateQueries({ queryKey: ['agendamentos'] });
      setResultado(r);
    },
  });

  if (resultado) return <Desfecho modo={modo} resultado={resultado} aoFechar={aoFechar} />;

  const abertos = (eventos.data?.data ?? []).filter((e) => e.status === SITUACAO_QUE_CONVIDA);

  return (
    <Modal
      titulo={modo === 'iniciar' ? 'Iniciar jornadas' : 'Agendar jornadas'}
      descricao="Vale para todos os participantes ativos que ainda não têm jornada em andamento."
      aoFechar={aoFechar}
      largura={560}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const dados = new FormData(e.currentTarget);

          disparar.mutate({
            evento: eventId ?? String(dados.get('evento')),
            quando:
              modo === 'agendar' ? new Date(String(dados.get('quando'))).toISOString() : undefined,
          });
        }}
      >
        {disparar.isError && <ErroBox erro={disparar.error} />}

        <div className="aviso" style={{ marginBottom: 16 }}>
          <i className="ti ti-alert-triangle" />
          <span>
            Isto manda mensagem de WhatsApp para pessoas de verdade, e{' '}
            <strong>não tem como desfazer</strong>. Quem já pediu para sair fica de fora
            automaticamente.
          </span>
        </div>

        {!eventId &&
          (eventos.isPending ? (
            <Carregando texto="Carregando eventos…" />
          ) : eventos.isError ? (
            <ErroBox erro={eventos.error} aoTentar={() => eventos.refetch()} />
          ) : (
            <Campo
              label="Evento"
              name="evento"
              obrigatorio
              autoFocus
              // Com um evento só, a escolha já vem feita.
              valorInicial={abertos.length === 1 ? abertos[0]!.id : ''}
              opcoes={[
                {
                  valor: '',
                  rotulo: abertos.length ? 'Escolha o evento' : 'Nenhum evento ativo — ative o evento primeiro',
                },
                ...abertos.map((e) => ({ valor: e.id, rotulo: e.name })),
              ]}
            />
          ))}

        {modo === 'agendar' && (
          <Campo
            label="Quando"
            name="quando"
            tipo="datetime-local"
            obrigatorio
            autoFocus={!!eventId}
            ajuda="Na hora do seu computador. Para mudar depois, é só agendar de novo — vale o último horário."
          />
        )}

        <ModalRodape>
          <button type="button" className="btn btn-ghost" onClick={aoFechar}>
            Cancelar
          </button>
          <button className="btn btn-primary" type="submit" disabled={disparar.isPending}>
            {disparar.isPending ? 'Enviando…' : modo === 'iniciar' ? 'Iniciar agora' : 'Agendar'}
          </button>
        </ModalRodape>
      </form>
    </Modal>
  );
}

/** Quantos entraram, e quem ficou de fora com o motivo — participante por participante. */
function Desfecho({
  modo,
  resultado: { quantos, para, recusadas },
  aoFechar,
}: {
  modo: ModoDeDisparo;
  resultado: Resultado;
  aoFechar: () => void;
}) {
  const frase = !quantos
    ? 'Ninguém entrou. Os motivos estão abaixo.'
    : modo === 'agendar'
      ? `${plural(quantos, 'pessoa recebe', 'pessoas recebem')} o convite em ${new Date(para!).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}.`
      : `${plural(quantos, 'convite entrou', 'convites entraram')} na fila de envio e sai${quantos === 1 ? '' : 'em'} em instantes.`;

  return (
    <Modal
      titulo={!quantos ? 'Ninguém foi incluído' : modo === 'agendar' ? 'Agendado' : 'Jornadas iniciadas'}
      aoFechar={aoFechar}
      largura={600}
    >
      <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-muted)', marginTop: 0 }}>{frase}</p>

      {recusadas.length > 0 && (
        <>
          <p style={{ fontSize: 13, fontWeight: 600, margin: '14px 0 8px' }}>
            Ficaram de fora ({recusadas.length}) — precisam de um ajuste antes
          </p>
          <div className="tabela-envolvente">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Participante</th>
                  <th>Motivo</th>
                </tr>
              </thead>
              <tbody>
                {recusadas.map((r) => (
                  <tr key={r.participantId}>
                    <td>{r.nome}</td>
                    <td style={{ fontSize: 12 }}>{r.motivo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <ModalRodape>
        <button className="btn btn-primary" onClick={aoFechar}>
          Entendi
        </button>
      </ModalRodape>
    </Modal>
  );
}
